import React, { memo } from 'react';
import { Box, Button, Typography, Paper, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// --- Colors & Theme Constants ---
export const TECH_BLUE = '#00f7ff';
export const TECH_ORANGE = '#ff9800';
export const DARK_BG = '#0a0a12';
export const GLASS_BG = 'rgba(16, 18, 27, 0.7)';
export const GLASS_BORDER = 'rgba(255, 255, 255, 0.08)';

// --- Animations ---
// Animation keyframes reserved for future use
const _glowPulse = keyframes`
  0% { box-shadow: 0 0 5px ${TECH_BLUE}40; }
  50% { box-shadow: 0 0 15px ${TECH_BLUE}60; }
  100% { box-shadow: 0 0 5px ${TECH_BLUE}40; }
`;

const _scanline = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
`;

// --- Components ---

// 1. Glass Panel (Floating Containers)
export const GlassPanel = styled(Paper)(({ theme: _theme, glow = false }) => ({
    background: GLASS_BG,
    backdropFilter: 'blur(12px)',
    border: `1px solid ${GLASS_BORDER}`,
    borderRadius: '16px', // Modern pill shape or rounded rect
    color: '#fff',
    transition: 'all 0.3s ease',
    boxShadow: glow ? `0 0 20px -5px ${TECH_BLUE}40` : '0 8px 32px rgba(0, 0, 0, 0.5)',
    overflow: 'hidden',
    position: 'relative',
    '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.15)',
        boxShadow: glow ? `0 0 25px -5px ${TECH_BLUE}60` : '0 12px 40px rgba(0, 0, 0, 0.6)',
    }
}));

// 2. Neon Button (Primary Actions)
export const NeonButton = styled(Button)(({ theme: _theme, variant = 'primary' }) => ({
    background: variant === 'primary' ? 'rgba(0, 247, 255, 0.1)' : 'transparent',
    border: `1px solid ${variant === 'primary' ? TECH_BLUE : 'rgba(255,255,255,0.2)'}`,
    color: variant === 'primary' ? TECH_BLUE : '#aaa',
    borderRadius: '8px',
    padding: '8px 16px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        background: variant === 'primary' ? 'rgba(0, 247, 255, 0.2)' : 'rgba(255,255,255,0.05)',
        boxShadow: variant === 'primary' ? `0 0 15px ${TECH_BLUE}60` : 'none',
        border: `1px solid ${variant === 'primary' ? TECH_BLUE : '#fff'}`,
        color: variant === 'primary' ? '#fff' : '#fff',
    },
    '&:active': {
        transform: 'scale(0.98)'
    }
}));

// 3. Tech Text (Monospace/Data)
export const TechText = styled(Typography)(({ theme: _theme, color = TECH_BLUE, size = '0.9rem' }) => ({
    fontFamily: '"Roboto Mono", "Consolas", monospace',
    color: color,
    fontSize: size,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    fontWeight: 500,
}));

// 4. IconButton with Glow
export const TechIconButton = styled(IconButton)(({ theme: _theme, active }) => ({
    color: active ? TECH_BLUE : 'rgba(255,255,255,0.6)',
    background: active ? 'rgba(0, 247, 255, 0.1)' : 'transparent',
    border: `1px solid ${active ? TECH_BLUE : 'transparent'}`,
    borderRadius: '8px',
    transition: 'all 0.2s',
    '&:hover': {
        background: 'rgba(0, 247, 255, 0.15)',
        color: '#fff',
        border: `1px solid ${TECH_BLUE}80`,
    }
}));

// 5. Grid Background Container
export const TechBackground = styled(Box)(({ theme: _theme }) => ({
    background: `
        linear-gradient(rgba(10, 10, 18, 0.9), rgba(10, 10, 18, 0.95)),
        repeating-linear-gradient(0deg, transparent, transparent 19px, rgba(0, 247, 255, 0.03) 20px),
        repeating-linear-gradient(90deg, transparent, transparent 19px, rgba(0, 247, 255, 0.03) 20px)
    `,
    backgroundColor: DARK_BG,
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 0,
    pointerEvents: 'none'
}));

// 6. Metric Pill (Displaying numeric values)
export const MetricPill = memo(({ label, value, unit = '' }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
            {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <TechText size="1rem" color="#fff" sx={{ mr: 0.5 }}>{value}</TechText>
            {unit && <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{unit}</Typography>}
        </Box>
    </Box>
));

MetricPill.displayName = 'MetricPill';
